// @flow strict

import * as React from 'react';

import throttle from '@ta/common.throttle';
import KeyboardEventListener from '@ta/events.keyboard-event-listener';

type Props = {|
  /**
   * An array of functions that resolve to React.Node. We do this so the children can be initialzed with the context of
   * if they are currently active as well as a (required) hook back up to this component for handling onMouseMove.
   */
  children: Array<
    (
      isActive: boolean,
      onMouseMove: (event: SyntheticMouseEvent<HTMLElement>) => mixed,
    ) => React.Node,
  >,
  /** Callback function for when a keyboard key is pressed */
  onKeyDown?: (event: SyntheticEvent<HTMLElement> | KeyboardEvent) => void,
|};

type State = {|
  /** The actual children we want to render after resolving this.props.children */
  children: React.Node,

  /** The currently active index in the typeahed result list */
  activeIndex: number,

  /** An array of child indices which can be navigated to */
  navigableIndices: Array<number>,
|};

const KEY_NAMES = ['ArrowUp', 'ArrowDown', 'Up', 'Down'];

/**
 * A prop name to set as a static member of a component to indicate it isn't navigable. This means it will be ignored
 * by mouse and keyboard navigation.
 */
export const NonNavigableItem = 'NonNavigableItem';

/**
 * A list that you can navigate with the mouse or keyboard. This component is intended to be used alongside
 * <NavigableListItem/>.
 *
 * The component responsibilities are carved up as follow:
 * 	• This component is responsible for navigation events and tracking the currently 'active' item
 * 	• <NavigableListItem/> is responsible for handling selection events.
 * 	• The children of <NavigableListItem/> are responsible for the actual visualization of the list item and doesn't
 * 	know anything about the navigation or selection events.
 *
 * Clicking on an item or hitting enter while it is 'active' will invoke the NavigableListItem.onSelection() callback.
 */
class NavigableList extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = this.stateFromProps(props, 0);
  }

  /* istanbul-ignore-next */
  componentDidUpdate(prevProps: Props) {
    if (prevProps !== this.props) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(this.stateFromProps(this.props, this.state.activeIndex));
    }
  }

  onKeyDown = (e: KeyboardEvent) => {
    // transpose the child index into the position in the list of navigable indices
    let position = this.state.navigableIndices.indexOf(this.state.activeIndex);
    if (position === -1) {
      position = 0;

      // IE9 and FF <= 36 uses 'Up' instead of 'ArrowUp' (etc.) so we need to normalize the names
    } else if (e.key.indexOf('Up') !== -1) {
      position = Math.max(position - 1, 0);

      // down
    } else {
      position = Math.min(position + 1, this.state.navigableIndices.length - 1);
    }

    if (this.props.onKeyDown) {
      this.props.onKeyDown(e);
    }

    // transpose back from position to actual child index
    const activeIndex = this.state.navigableIndices[position];
    this.setState(this.stateFromProps(this.props, activeIndex));
    e.preventDefault();
  };

  /**
   * Creates a new state object from props and an active index (but doesn't set it). Because we are storing the actual
   * children to be rendered in the state we need to call this method whenever we are going to call this.setState()
   * otherwise it will appear like it didn't re-render.
   */
  stateFromProps = (props: Props, activeIndex: number) => {
    let index = 0;
    let actualActiveIndex = activeIndex;
    const navigableIndices = [];

    const children: React.Node = props.children.map(childFn => {
      const isActive = index === actualActiveIndex;
      const onMouseMove = this.newMouseMoveCallback(index);
      const child = childFn(isActive, onMouseMove);

      // Only include the indices of children who don't have the 'NonNavigableItem' flag
      // $FlowFixMe: https://github.com/facebook/flow/issues/5753
      if (!child || !child.type || typeof child.type[NonNavigableItem] === 'undefined') {
        navigableIndices.push(index);

        // else if the index we want to be active cannot be active
      } else if (isActive) {
        actualActiveIndex += 1;
      }

      index += 1;
      return child;
    });

    return {
      children,
      activeIndex: actualActiveIndex,
      navigableIndices,
    };
  };

  /**
   * Create a new callback appropriate for updating the activeIndex to a given index
   */
  // Skipping test coverage for this function since it is not possible to unit test it correctly
  newMouseMoveCallback = /* istanbul ignore next */ (index: number) => {
    const i = index;
    return throttle(() => {
      if (this.state.navigableIndices.indexOf(i) !== -1) {
        this.setState(this.stateFromProps(this.props, i));
      }
    }, 100);
  };

  render() {
    return (
      <React.Fragment>
        <KeyboardEventListener keyName={KEY_NAMES} callback={this.onKeyDown} />
        {this.state.children}
      </React.Fragment>
    );
  }
}

export default NavigableList;
